import { EstablishmentCompanyData, EstablishmentProductPrice } from "@/common/types/establishment/company.type";
import { formatCurrency } from "@/common/utils/formatters";
import { formatDateTimePriceUpdatedAt } from "../../utils/formatters";
import { unities } from "@/common/constants/unities.constant";
import { useEstablishmentProductPrices } from "@/features/establishments/hooks/useEstablishmentProductPrices";
import { twMerge } from "tailwind-merge";
import { useEstablishmentMap } from "../../stores/useEstablishmentMap.store";
import React from "react";
import { Tooltip } from "react-tooltip";
import { LoadingEstablishmentTooltipProductPrices } from "./LoadingTooltipDetails";
import { useNavigate } from "react-router-dom";
import { animatedPanTo } from "../../utils/animatedPanTo";
import { smoothZoomIn } from "../../utils/smoothZoom";

export const EstablishmentTooltipDetails = ({
  establishment,
  cardList,
  setFilterBounds,
}: {
  establishment?: EstablishmentCompanyData;
  cardList?: boolean;
  setFilterBounds?: (bounds: { top_left_lat: number; top_left_lng: number; bottom_right_lat: number; bottom_right_lng: number; }) => void;
}) => {
  const navigate = useNavigate();

  const {
    map,
    colorScheme,
    hoveredMarkerId,
    updateHoveredMarkerId,
    updateHoveredOnList,
    updateBoundsChangedOnCardClick,
    updateHoveredEstablishmentData,
    clusteredEstablishmentsIds,
    updateIsAnimating,
  } = useEstablishmentMap();

  const { data: productPrices, isFetching: isProductPricesFetching } = useEstablishmentProductPrices<EstablishmentProductPrice[]>({
    enabled: !cardList,
    filters: { branchId: establishment?.matrix_branch_id }
  });

  const establishmentAddress = establishment?.general_information?.address;
  const hasEstablishmentCoordinates = establishmentAddress?.latitude && establishmentAddress?.longitude;
  const establishmentCoordinates = { lat: Number(establishmentAddress?.latitude), lng: Number(establishmentAddress?.longitude) };

  const handleMouseLeave = () => {
    updateHoveredMarkerId('');
    updateHoveredEstablishmentData(undefined);
  };

  const handleMouseEnter = () => {
    if (cardList) {
      updateHoveredOnList(true);
      updateHoveredMarkerId(establishment?.id);
      updateHoveredEstablishmentData(establishment);
    }
  };

  const handleClick = async () => {
    if (cardList && hasEstablishmentCoordinates) {
      updateBoundsChangedOnCardClick(true);
      updateHoveredOnList(true);

      if (!clusteredEstablishmentsIds?.includes(establishment?.id || '') && map) {
        await animatedPanTo({
          map,
          newCenter: establishmentCoordinates,
          onStartAnimation: () => updateIsAnimating(true),
          onEndAnimation: () => updateIsAnimating(false),
          onSetFinalPosition: (finalBounds) => setFilterBounds?.({
            top_left_lat: finalBounds.topLeft.lat,
            top_left_lng: finalBounds.topLeft.lng,
            bottom_right_lat: finalBounds.bottomRight.lat,
            bottom_right_lng: finalBounds.bottomRight.lng,
          }),
        });
      } else {
        map?.panTo(new google.maps.LatLng(establishmentCoordinates.lat, establishmentCoordinates.lng));

        const currentZoom = map?.getZoom();

        if (map && currentZoom && currentZoom < 12) {
          await smoothZoomIn({
            map,
            initialZoom: currentZoom,
            finalZoom: 12,
          });
        }
      }

      updateHoveredMarkerId(establishment?.id);
      updateHoveredEstablishmentData(establishment);
    } else {
      navigate(`/establishments/${establishment?.id}`);
    }
  };

  return (
    <>
      {establishment?.matrix_branch?.photos[0]?.url && (
        <div
          className={`w-full h-[148px] rounded-t-md bg-[url('${establishment.matrix_branch.photos[0]?.url}')]`}
          style={{ backgroundImage: `url(${establishment.matrix_branch.photos[0]?.url})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
        />
      )}

      <div
        data-testid={cardList ? 'establishment_details_card' : 'establishment_tooltip_card'}
        className={twMerge(
          'w-[336px] flex flex-col cursor-pointer',
          hoveredMarkerId === establishment?.id && cardList && 'bg-sky-600/20 transition rounded-md establishment-card-hovered',
        )}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        <div
          className={twMerge(
            'flex flex-col p-3 bg-slate-50/10',
            !establishment?.matrix_branch?.photos[0]?.url && 'rounded-t-md',
            (!Number(productPrices?.length)) && 'rounded-b-md',
          )}
        >
          <div className="flex gap-2 items-center">
            {establishment?.general_information?.company_logo?.url && (
              <img src={establishment.general_information.company_logo?.url} className="w-[36px] h-[36px] rounded-full" />
            )}
            <p className={twMerge('font-bold text-lg', cardList && 'text-sm')} data-testid={cardList ? 'establishment_details_card_display_name' : 'establishment_map_tooltip_details_display_name'}>{establishment?.general_information?.fantasy_name}</p>
          </div>
          <p className={twMerge('font-light mt-1 text-sm -skew-x-6', cardList && 'text-xs')}>
            {`${establishment?.general_information?.address.street},
            ${establishment?.general_information?.address.number} -
            ${establishment?.general_information?.address.city?.state?.acronym},
            ${establishment?.general_information?.address.city?.display_name}`}
          </p>
        </div>

        {!cardList && (
          <>
            {isProductPricesFetching && <LoadingEstablishmentTooltipProductPrices cardList={cardList} />}

            {(!isProductPricesFetching && Number(productPrices?.length) > 0) && (
              <div className={twMerge('flex-col p-3 rounded-b-md', colorScheme === 'dark' && cardList && 'bg-white-100')}>
                <div className="flex items-end">
                  {productPrices?.slice(0, 3).map((productPrice, index) => (
                    <React.Fragment key={productPrice.id}>
                      {index > 0 && <div className="bg-gray-500 w-[1px] h-10 mr-2 self-center" />}

                      <div className="flex-col w-1/3" data-tooltip-id={productPrice.id} data-testid="establishment_tooltip_details_product_price">
                        <p className="font-bold text-xs text-nowrap max-w-28 overflow-hidden text-ellipsis">
                          {productPrice.product.display_name}
                        </p>

                        <p className={twMerge('font-normal text-lg max-w-24 overflow-hidden text-ellipsis', cardList && 'text-sm')}>
                          {formatCurrency(Number(productPrice.value) || 0)}

                          <span className="font-light text-sm text-white-400">
                            {unities[productPrice.product.unit_for_quantity] ? `/` : ''}{unities[productPrice.product.unit_for_quantity]}
                          </span>
                        </p>

                        <p className={twMerge('font-thin italic -skew-x-6 text-white-300 text-nowrap', cardList && 'text-[10px]')}>
                          {productPrice.updated_at && formatDateTimePriceUpdatedAt(new Date(productPrice.updated_at))}
                        </p>

                        {(`${formatCurrency(Number(productPrice.value) || 0)}/${unities[productPrice.product.unit_for_quantity]}`.length > 10) && (
                          <Tooltip id={productPrice.id}>
                            <div className="flex flex-col items-center gap-2">
                              <p className={twMerge('font-thin italic text-white-400', cardList && 'text-xs')}>
                                {productPrice.product.display_name}
                              </p>

                              <p className={twMerge('font-normal text-lg', cardList && 'text-sm')}>
                                {formatCurrency(Number(productPrice.value) || 0)}

                                <span className="font-light text-sm text-white-400">
                                  {unities[productPrice.product.unit_for_quantity] ? `/` : ''}{unities[productPrice.product.unit_for_quantity]}
                                </span>
                              </p>
                            </div>
                          </Tooltip>
                        )}
                      </div>
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};
